import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, ExternalLink, Loader2, ChevronDown, Mail, ArrowRight } from 'lucide-react';
import { getSession, submitEmail, SessionData } from '../services/session';
import ArmyLogo from './united-states-army-2023-seeklogo.png';

interface ResultPageProps {
  sessionId: string;
}

const ResultPage: React.FC<ResultPageProps> = ({ sessionId }) => {
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submittingEmail, setSubmittingEmail] = useState(false);

  // Get mosId from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const mosId = urlParams.get('mos');

  // Helper to get the correct image URLs (MOS-specific or fallback)
  const getImageUrls = (sessionData: SessionData) => {
    // If mosId is provided and we have mos_images for that MOS, use them
    if (mosId && sessionData.mos_images?.[mosId]) {
      return sessionData.mos_images[mosId];
    }
    // Fallback to default URLs (backwards compatibility)
    return {
      portrait: sessionData.portrait_photo_url,
      field: sessionData.field_photo_url
    };
  };

  // Enable scrolling on result page (override global overflow: hidden)
  useEffect(() => {
    // Save original styles
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    const originalRootOverflow = document.getElementById('root')?.style.overflow;

    // Enable scrolling
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.style.overflow = 'auto';
    }

    // Restore on unmount
    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      if (rootEl) {
        rootEl.style.overflow = originalRootOverflow || '';
      }
    };
  }, []);

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching session:', sessionId);
        const response = await getSession(sessionId);
        console.log('Session response:', response);

        if (response.success && response.session) {
          console.log('Session data:', {
            id: response.session.id,
            session_code: response.session.session_code,
            portrait_photo_url: response.session.portrait_photo_url,
            field_photo_url: response.session.field_photo_url,
            status: response.session.status,
            email: response.session.email,
          });
          setSession(response.session);
          // If email already exists, skip email entry
          if (response.session.email) {
            setEmailSubmitted(true);
          }
        } else {
          console.error('Session fetch failed:', response.error);
          setError(response.error || 'Session not found');
        }
      } catch (err) {
        console.error('Session fetch exception:', err);
        setError('Failed to load session');
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    if (!email.trim()) {
      setEmailError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setSubmittingEmail(true);
    try {
      const response = await submitEmail(sessionId, email);
      if (response.success) {
        setEmailSubmitted(true);
      } else {
        setEmailError(response.error || 'Failed to submit email');
      }
    } catch (err) {
      setEmailError('An error occurred. Please try again.');
    } finally {
      setSubmittingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-army-black flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-army-gold animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your photos...</p>
        </motion.div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-army-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <img src={ArmyLogo} alt="U.S. Army" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-4">Session Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error || 'The session you are looking for does not exist or has expired.'}
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-army-gold text-army-black font-bold rounded hover:bg-army-gold/90 transition-colors"
          >
            Start Your Own Experience
          </a>
        </motion.div>
      </div>
    );
  }

  // Get the correct image URLs for this MOS
  const images = getImageUrls(session);
  const hasPortrait = !!images.portrait;
  const hasField = !!images.field;
  const hasPhotos = hasPortrait || hasField;
  const hasBothPhotos = hasPortrait && hasField;

  // Log image load errors
  const handleImageError = (type: string, url: string) => {
    console.error(`Failed to load ${type} image:`, url);
  };

  if (!hasPhotos) {
    return (
      <div className="min-h-screen bg-army-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <img src={ArmyLogo} alt="U.S. Army" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-4">Photos Not Ready</h1>
          <p className="text-gray-400 mb-6">
            Your photos are still being generated. Please check back in a moment.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-block px-6 py-3 bg-army-gold text-army-black font-bold rounded hover:bg-army-gold/90 transition-colors"
          >
            Refresh
          </button>
        </motion.div>
      </div>
    );
  }

  // Email entry gate - show before revealing photos
  if (!emailSubmitted) {
    return (
      <div className="min-h-screen bg-army-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md w-full"
        >
          <img src={ArmyLogo} alt="U.S. Army" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">
            YOUR PHOTOS ARE READY
          </h1>
          <p className="text-gray-400 mb-6">
            Enter your email address to view and download your Army portraits
          </p>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full pl-12 pr-4 py-4 bg-army-dark border-2 border-army-gold/50 rounded-lg text-white placeholder-gray-500 focus:border-army-gold focus:outline-none text-lg"
                autoComplete="email"
                autoFocus
              />
            </div>

            {emailError && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm"
              >
                {emailError}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={submittingEmail}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-army-gold text-army-black font-bold rounded-lg hover:bg-army-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {submittingEmail ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  View My Photos
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-xs text-gray-500">
            Your email will only be used to send you information about Army opportunities.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-army-black">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-10 bg-army-dark border-b border-army-gold/30 p-4"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <img src={ArmyLogo} alt="U.S. Army" className="h-10 md:h-12" />
        </div>
      </motion.header>

      {/* Content */}
      <main className="max-w-lg mx-auto p-4 pb-8">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            YOUR ARMY PORTRAITS
          </h1>
          {session.selected_mos_title && (
            <p className="text-army-gold uppercase tracking-wider text-sm">
              {session.selected_mos_title}
            </p>
          )}
          {session.last_name && (
            <p className="text-gray-400 mt-1">
              SGT {session.last_name}
            </p>
          )}
        </motion.div>

        {/* Scroll indicator for mobile when both photos exist */}
        {hasBothPhotos && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center mb-4 text-gray-400 text-sm flex items-center justify-center gap-1"
          >
            <ChevronDown className="w-4 h-4 animate-bounce" />
            <span>Scroll down for both photos</span>
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </motion.div>
        )}

        {/* Portrait Photo */}
        {hasPortrait && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="relative aspect-[3/4] bg-army-dark rounded-lg overflow-hidden border-2 border-army-gold shadow-lg shadow-army-gold/20">
              <img
                src={images.portrait!}
                alt="Official Portrait"
                className="w-full h-full object-cover"
                onError={() => handleImageError('portrait', images.portrait!)}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-sm text-army-gold uppercase tracking-wider font-bold">
                  Official Portrait
                </p>
              </div>
            </div>
            <button
              onClick={() => handleDownload(
                images.portrait!,
                `army-portrait-${session.session_code}.jpg`
              )}
              className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-4 bg-army-gold text-army-black font-bold rounded-lg hover:bg-army-gold/90 transition-colors text-lg"
            >
              <Download className="w-6 h-6" />
              Download Portrait
            </button>
          </motion.div>
        )}

        {/* Field Photo */}
        {hasField && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <div className="relative aspect-[3/4] bg-army-dark rounded-lg overflow-hidden border-2 border-hud-green shadow-lg shadow-hud-green/20">
              <img
                src={images.field!}
                alt="Field Action"
                className="w-full h-full object-cover"
                onError={() => handleImageError('field', images.field!)}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-sm text-hud-green uppercase tracking-wider font-bold">
                  Field Action
                </p>
              </div>
            </div>
            <button
              onClick={() => handleDownload(
                images.field!,
                `army-field-${session.session_code}.jpg`
              )}
              className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-4 bg-hud-green text-army-black font-bold rounded-lg hover:bg-hud-green/90 transition-colors text-lg"
            >
              <Download className="w-6 h-6" />
              Download Field Photo
            </button>
          </motion.div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center"
        >
          <div className="bg-army-dark border border-army-gold/30 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-2">
              Ready to Make This Vision a Reality?
            </h2>
            <p className="text-gray-400 mb-4 text-sm">
              Learn more about becoming a U.S. Army Soldier
            </p>
            <a
              href="https://www.goarmy.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-army-gold text-army-black font-bold rounded hover:bg-army-gold/90 transition-colors"
            >
              Visit GOARMY.COM
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>

        {/* Start Your Own */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-6 text-center"
        >
          <a
            href="/"
            className="text-army-gold hover:text-army-gold/80 transition-colors underline"
          >
            Start Your Own Experience
          </a>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-army-gold/20">
        <p className="text-center text-xs text-gray-500">
          Session: {session.session_code} • MOS.AIC
        </p>
      </footer>
    </div>
  );
};

export default ResultPage;
